import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getBrands } from '../../api/getBrands/getBrands';
import styles from './Brand.module.scss';
import {
  BreadcrumbHeading,
  DetailsPanel,
  EditIcon,
  EmptyStateButton,
  FormatInformation,
  IconButton,
  Prompt,
  Spinner,
  useNotifications,
} from '@weareopenr/component-library';
import { IConfiguredAddons } from 'src/api/getBrands/types';
import { getLogo } from 'src/utils/getLogo';
import ChannelModal from 'src/components/ChannelModal';
import { TVariant } from 'src/types';
import { ChannelSummary } from '../../components/ChannelSummary/ChannelSummary';
import { deleteChannel } from '../../api/deleteChannel/deleteChannel';
import BrandModal from 'src/components/BrandModal';
import { usePermissions, useUserTracking } from 'src/hooks';

export const Brand = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const createNotification = useNotifications();
  const [channelToDelete, setChannelToDelete] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isChannelModalOpen, setIsChannelModalOpen] = useState<boolean>(false);
  const [variant, setVariant] = useState<TVariant>('Add');
  const [isBrandModalOpen, setIsBrandModalOpen] = useState<boolean>(false);
  const { canEditBrands, canInstallExtensions, canUninstallExtensions } = usePermissions();
  const { trackEvent } = useUserTracking();

  const handleChannelModalToggle = (): void => {
    canEditBrands && setIsChannelModalOpen((prev: boolean) => !prev);
  };
  const handleBrandModalToggle = (): void => {
    if (canEditBrands) {
      setIsBrandModalOpen((prev) => {
        if (!prev) {
          trackEvent('brand_edit_modal_opened', { brand_id: brandId });
        }
        return !prev;
      });
    }
  };

  const onChannelModal = (value: TVariant, id?: string): void => {
    if (id) {
      navigate(location.pathname, { state: id });
    }
    setVariant(value);
    handleChannelModalToggle();
  };

  const { data, refetch } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    onError: () =>
      createNotification({
        severity: 'error',
        message: 'Something went wrong whilst fetching brands',
      }),
  });

  const { mutate } = useMutation((channelId: string) => deleteChannel(channelId), {
    onSuccess: (_data, channelId) => {
      const deletedChannel = currentBrand?.configured_addons.find(
        (addon) => addon.configuration_id === channelId,
      );
      trackEvent('channel_deleted', {
        channel_type: deletedChannel?.addon_name,
        brand_id: brandId,
      });
      setChannelToDelete(null);
      createNotification({ severity: 'success', message: 'Channel successfully deleted' });
      refetch();
    },
    onError: () => {
      createNotification({
        severity: 'error',
        message: 'Something went wrong whilst deleting channel',
      });
    },
  });

  const currentBrand = data?.find((brand) => brand?.brand_id === brandId);

  return (
    <>
      {currentBrand ? (
        <>
          <BreadcrumbHeading
            breadcrumbs={[
              { label: 'Brands', path: '/' },
              { label: currentBrand.name, path: '' },
            ]}
          />
          <div className={styles['details-container']}>
            <DetailsPanel
              title="Details"
              action={
                canEditBrands ? (
                  <IconButton
                    onClick={handleBrandModalToggle}
                    aria-label="Edit brand details"
                    icon={EditIcon}
                  />
                ) : undefined
              }
            >
              <DetailsPanel.Row>
                <DetailsPanel.Column>
                  <FormatInformation heading="Name">{currentBrand.name}</FormatInformation>
                  <FormatInformation heading="Venues">
                    {currentBrand.venues > 0 ? String(currentBrand.venues) : 'No Venues'}
                  </FormatInformation>
                </DetailsPanel.Column>
                <DetailsPanel.Column>
                  <img
                    className={styles['logo-image']}
                    src={getLogo(currentBrand?.logo_url, '')}
                    alt={currentBrand.name}
                  />
                </DetailsPanel.Column>
              </DetailsPanel.Row>
            </DetailsPanel>
            <DetailsPanel title="Channels">
              {currentBrand?.configured_addons.map((addon: IConfiguredAddons) => (
                <ChannelSummary
                  key={addon.configuration_id}
                  channelId={addon.configuration_id}
                  channelName={addon.preferred_name}
                  onDeleteClick={setChannelToDelete}
                  onChannelModal={onChannelModal}
                />
              ))}
              {canInstallExtensions && (
                <>
                  <ChannelModal
                    variant={variant}
                    open={isChannelModalOpen}
                    closeModal={handleChannelModalToggle}
                    currentBrand={currentBrand}
                    refetch={refetch}
                    configurationId={location.state ?? undefined}
                  />
                  <EmptyStateButton label="+ Add" onClick={() => onChannelModal('Add')} />
                </>
              )}
            </DetailsPanel>
          </div>

          {canEditBrands && (
            <BrandModal
              open={isBrandModalOpen}
              closeModal={handleBrandModalToggle}
              refetch={refetch}
              brands={data}
              variant="update"
              currentBrand={currentBrand}
            />
          )}
          {canUninstallExtensions && (
            <Prompt
              closePrompt={() => setChannelToDelete(null)}
              open={Boolean(channelToDelete)}
              buttonHandler={() => mutate(channelToDelete || '')}
              buttonText="Delete"
              promptHeading={`Are you sure you want to delete ${
                currentBrand.configured_addons.find(
                  (addon) => addon.configuration_id === channelToDelete,
                )?.addon_name
              }?`}
            >
              You will permanently remove this channel.
            </Prompt>
          )}
        </>
      ) : (
        <div className={styles['spinner']}>
          <Spinner size="xlarge" colour="rainbow" />
        </div>
      )}
    </>
  );
};
